import { IAnnualProgramDiscountType } from '@/_types'

import type { IAnnualProgramDiscountData } from '@/app/[locale]/order/_types/annual-program-discount'
import type {
	AnnualProgramPriceHighlight,
	PriceCalculationResult,
	TimeSlotDetailShape
} from '@/app/[locale]/order/_utils/price-calculation.types'

export type {
	AnnualProgramPriceHighlight,
	PriceCalculationResult,
	TimeSlotDetailShape
} from '@/app/[locale]/order/_utils/price-calculation.types'

/** 10% discount on template price when annual program allows online booking with discount. */
const ANNUAL_TEMPLATE_DISCOUNT_MULTIPLIER = 0.9 as const

/**
 * Finds the template row for the selected insurance company, if any.
 *
 * @param timeSlotDetail - Time-slot payload with `data.template.insuranceCompanies`.
 * @param selectedInsuranceId - Stringified insurer id to match against `insuranceCompany.id`.
 * @returns Matching `{ price, description, insuranceCompany }` entry, or `null`.
 */
function findTemplateInsuranceEntry(
	timeSlotDetail: TimeSlotDetailShape | null | undefined,
	selectedInsuranceId: string | null | undefined
) {
	const template = timeSlotDetail?.data?.template
	if (!selectedInsuranceId || !template?.insuranceCompanies) return null
	const entry =
		template.insuranceCompanies.find(insurance => {
			if (!insurance?.insuranceCompany?.id) {
				console.warn('Invalid insurance data structure:', insurance)
				return false
			}
			return insurance.insuranceCompany.id.toString() === selectedInsuranceId
		}) ?? null
	return entry
}

/**
 * Resolves the insurer-specific template price when a company is selected; otherwise the template base `price`.
 *
 * @param timeSlotDetail - Time-slot detail containing `data.template` and optional `insuranceCompanies` rows.
 * @param selectedInsuranceId - Selected insurer id as string (matched against `insuranceCompany.id`); omit for base template price only.
 * @returns Numeric template price before annual-program discounts, or `null` if neither insurer row nor base `price` exists.
 */
export const getTemplatePriceBeforeAnnualProgram = (
	timeSlotDetail: TimeSlotDetailShape | null | undefined,
	selectedInsuranceId: string | null | undefined
): number | null => {
	const template = timeSlotDetail?.data?.template
	const entry = findTemplateInsuranceEntry(timeSlotDetail, selectedInsuranceId)
	if (entry?.price != null) return entry.price
	return template?.price ?? null
}

function applyAnnualProgramTemplateRules(
	price: number | null,
	annualProgramDiscountData: IAnnualProgramDiscountData | null
): { price: number | null; highlight: AnnualProgramPriceHighlight | null } {
	if (!annualProgramDiscountData?.canDiscountTemplate) {
		return { price, highlight: null }
	}

	const programName = annualProgramDiscountData.annualProgram.name

	switch (annualProgramDiscountData.discountType) {
		case IAnnualProgramDiscountType.NO_PAYMENT:
			return {
				price: 0,
				highlight: { type: 'NO_PAYMENT', programName }
			}
		case IAnnualProgramDiscountType.DISCOUNT_10:
			return {
				price: (price ?? 0) * ANNUAL_TEMPLATE_DISCOUNT_MULTIPLIER,
				highlight: { type: 'DISCOUNT_10', programName }
			}
		case IAnnualProgramDiscountType.UNABLE_TO_CALCULATE:
			return {
				price,
				highlight: { type: 'UNABLE_TO_CALCULATE', programName }
			}
		case IAnnualProgramDiscountType.NO_DISCOUNT:
			return { price, highlight: null }
		default: {
			const _exhaustive: never = annualProgramDiscountData.discountType
			void _exhaustive
			return { price, highlight: null }
		}
	}
}

/**
 * Price and copy for doctor / time-slot ambulance flows: insurer row + optional annual-program discount rules.
 *
 * @param timeSlotDetail - Same as {@link getTemplatePriceBeforeAnnualProgram}.
 * @param selectedInsuranceId - Selected insurer id string, or absent for base pricing.
 * @param annualProgramDiscountData - Eligibility and discount type from annual program verification; may zero price, apply 10%, or set messaging only.
 * @returns `{ price, description, annualProgramHighlight? }` — `description` from insurer row; highlight is structured for UI copy.
 */
export const calculateDoctorAmbulancePrice = (
	timeSlotDetail: TimeSlotDetailShape | null | undefined,
	selectedInsuranceId: string | null | undefined,
	annualProgramDiscountData: IAnnualProgramDiscountData | null
): PriceCalculationResult => {
	const insuranceEntry = findTemplateInsuranceEntry(timeSlotDetail, selectedInsuranceId)
	const template = timeSlotDetail?.data?.template

	let price: number | null =
		insuranceEntry?.price != null ? insuranceEntry.price : (template?.price ?? null)
	const description = insuranceEntry?.description ?? ''

	const { price: nextPrice, highlight } = applyAnnualProgramTemplateRules(
		price,
		annualProgramDiscountData
	)

	return {
		price: nextPrice,
		description,
		annualProgramHighlight: highlight
	}
}
